import { NotificationType } from '@app/components/common/BaseNotification/BaseNotification';
import { Priority } from '@app//constants/enums/priorities';
import { useSearchParams } from 'react-router-dom';
import visa from '@app/assets/images/card-issuers/visa.png';
import mastercard from '@app/assets/images/card-issuers/mastercard.png';
import maestro from '@app/assets/images/card-issuers/maestro.png';
import { CurrencyTypeEnum, Severity } from '@app/interfaces/interfaces';
import { BaseBadgeProps } from '@app/components/common/BaseBadge/BaseBadge';
import { currencies } from '@app/constants/config/currencies';

export type SetURLSearchParams = ReturnType<typeof useSearchParams>[1];

export const camelize = (string: string): string => {
  return string
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)))
    .join('');
};

export const getCurrencyPrice = (price: number | string, currency: CurrencyTypeEnum, isIcon = true): string => {
  const currencySymbol = currencies[currency][isIcon ? 'icon' : 'text'];

  return isIcon ? `${currencySymbol}${price}` : `${price} ${currencySymbol}`;
};

type MarkArea = {
  xAxis: number;
};

export const getMarkAreaData = (data: string[] | number[]): MarkArea[][] =>
  data.map((el, index) => [
    {
      xAxis: 2 * index,
    },
    {
      xAxis: 2 * index + 1,
    },
  ]);

export const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

export const hexToRGB = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return `${r}, ${g}, ${b}`;
};

export const getDifference = (value: number, prevValue: number): string | null =>
  prevValue !== 0 ? `${((Math.abs(value - prevValue) / prevValue) * 100).toFixed(0)}%` : '100%';

// Stocking plan difference is stored as plan - stocked, so it's flipped and sign-prefixed
// for display: overstocking (stocked > plan) reads as a positive surplus, e.g. "+600".
export const formatSignedDifference = (value: number): string => {
  const negated = -(value || 0);
  return negated > 0 ? `+${negated}` : `${negated}`;
};

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === 'number' ? `${prop}px` : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

export const defineColorByPriority = (priority: Priority): string => {
  switch (priority) {
    case Priority.INFO:
      return 'var(--primary-color)';
    case Priority.LOW:
      return 'var(--success-color)';
    case Priority.MEDIUM:
      return 'var(--warning-color)';
    case Priority.HIGH:
      return 'var(--error-color)';
    default:
      return 'var(--success-color)';
  }
};

export const defineColorBySeverity = (severity: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (severity) {
    case 'error':
    case 'warning':
    case 'success':
      return `var(--${severity}-${postfix})`;
    case 'info':
    default:
      return `var(--primary-${postfix})`;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeBy = (a: any[], b: any[], key: string): any[] =>
  a.filter((elem) => !b.find((subElem) => subElem[key] === elem[key])).concat(b);

export const getSmoothRandom = (factor: number, start: number): number => {
  const halfEnvelope = 1 / factor / 2;
  const max = Math.min(1, start + halfEnvelope);
  const min = Math.max(0, start - halfEnvelope);

  return Math.random() * (max - min) + min;
};

export const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString());
  G = parseInt(((G * (100 + percent)) / 100).toString());
  B = parseInt(((B * (100 + percent)) / 100).toString());

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    if (max == min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return {
      h,
      s,
      l,
    };
  } else {
    throw new Error('Non valid HEX color');
  }
};

export const formatNumberWithCommas = (value: number): string => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
export const msToH = (ms: number): number => Math.floor(ms / 3600000);

export const hToMS = (h: number): number => h * 3600000;

export const getPaymentCardTypeIcon = (type: string): string | null => {
  switch (type) {
    case 'visa':
      return visa;
    case 'mastercard':
      return mastercard;
    case 'maestro':
      return maestro;
    case 'amex':
      return 'amex';
    case 'discover':
      return 'discover';
    case 'diners':
      return 'diners';
    case 'jcb':
      return 'jcb';
    case 'unionpay':
      return 'unionpay';
    default:
      return null;
  }
};

export const mapBadgeStatus = (status: BaseBadgeProps['status']): Severity => {
  if (!status || status === 'default' || status === 'processing') {
    return 'info';
  }

  return status;
};

const getPropertyName = <T>(basePropertyName: string, locale: string, format: 'capitalize' | 'upper'): keyof T => {
  let formattedLocale: string;
  switch (format) {
    case 'capitalize':
      formattedLocale = locale.charAt(0).toUpperCase() + locale.slice(1);
      break;
    case 'upper':
      formattedLocale = locale.toUpperCase();
      break;
    default:
      formattedLocale = locale;
  }

  const propertyName = (
    format === 'capitalize' ? `${basePropertyName}${formattedLocale}` : `${basePropertyName}_${formattedLocale}`
  ) as keyof T;
  return propertyName;
};

export const getLocalizedValue = <T>(
  item: T,
  basePropertyName: string,
  locale: string,
  format: 'capitalize' | 'upper',
): string => {
  const propertyName = getPropertyName<T>(basePropertyName, locale, format);

  return (item[propertyName] as string) || '';
};

export const stableSort = <T>(array: T[], comparator: (a: T, b: T) => number): T[] => {
  return array
    .map((el, index) => [el, index] as [T, number])
    .sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    })
    .map((el) => el[0]);
};

export const exportToCSV = (data: (string | number)[][], filename = 'data.csv') => {
  const csvContent = data
    .map((row) =>
      row
        .map((cell) => {
          const cellString = String(cell);

          // wrap in double quotes if it contains commas, newlines, or quotes
          if (/[,"\n]/.test(cellString)) {
            return `"${cellString.replace(/"/g, '""')}"`;
          }
          return cellString;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToExcel = async (data: (string | number)[][], filename = 'data.xlsx') => {
  const ExcelJS = await import('exceljs');

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  data.forEach((row) => {
    worksheet.addRow(row);
  });

  worksheet.columns.forEach((column) => {
    let maxLength = 0;
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const cellValue = cell.value ? String(cell.value) : '';
      maxLength = Math.max(maxLength, cellValue.length);
    });
    column.width = Math.min(Math.max(maxLength + 2, 10), 50);
  });

  if (data.length > 0) {
    // Always style the first row as a header
    const firstRow = worksheet.getRow(1);
    firstRow.font = { bold: true };
    firstRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' },
    };

    // Detect and style additional header rows (rows after empty rows)
    for (let i = 1; i < data.length - 1; i++) {
      const currentRow = data[i];
      const nextRow = data[i + 1];

      // If current row is empty and next row has content, next row is likely a header
      const isCurrentEmpty = currentRow.every((cell) => cell === '' || cell === null || cell === undefined);
      const isNextNotEmpty = nextRow && nextRow.some((cell) => cell !== '' && cell !== null && cell !== undefined);

      if (isCurrentEmpty && isNextNotEmpty) {
        const headerRow = worksheet.getRow(i + 2); // +2 because Excel rows are 1-indexed and we're looking at next row
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0E0E0' },
        };
      }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(link.href);
};

export const equalsAfterSort = <T extends number | string>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  const sortedB = b.slice().sort();
  return a
    .slice()
    .sort()
    .every((el, index) => el === sortedB[index]);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getPropAtPath = (path: string, obj: Record<string, any>): any => {
  if (!path) return obj;

  return path.split('.').reduce((acc, key) => acc && acc[key], obj);
};

export const formatInt = (val: string, allowNegative = false, allowZero = false) => {
  const isNegative = val.trim().startsWith('-') && allowNegative;
  const numericStr = val.replace(/\D/g, '');
  if (!numericStr) return '';
  // Most numeric inputs treat 0 as "empty"; opt in with `allowZero` where 0 is a real value.
  if (!allowZero && +numericStr === 0) return '';

  const result = Number(numericStr).toFixed(0).toString();

  return isNegative ? -result : result;
};

export const formatFloat = (val: string, allowNegative = false) => {
  const isNegative = val.trim().startsWith('-') && allowNegative;
  const numericStr = val.replace(/[^0-9.]/g, '');
  const parts = numericStr.split('.');

  const result = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : numericStr;

  return isNegative ? `-${result}` : result;
};

export const formatDuration = (startTime: string, endTime: string): string => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '';
  }

  const diffMs = end.getTime() - start.getTime();

  if (diffMs <= 0) {
    return '';
  }

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  return parts.join(' ') || '0m';
};

export const downloadDocument = (data: Blob | ArrayBuffer | string, filename?: string, contentType?: string) => {
  let blob: Blob;

  if (data instanceof Blob) {
    blob = data;
  } else if (data instanceof ArrayBuffer) {
    blob = new Blob([data], { type: contentType || 'application/octet-stream' });
  } else {
    blob = new Blob([data], { type: contentType || 'text/plain' });
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  if (filename) {
    link.setAttribute('download', filename);
  } else {
    link.setAttribute('download', 'download');
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
};

export const extractFilenameFromContentDisposition = (contentDisposition: string): string | null => {
  if (!contentDisposition) return null;

  // Match filename="..." or filename*=UTF-8''...
  const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]*)/);

  if (filenameStarMatch) {
    return decodeURIComponent(filenameStarMatch[1]);
  }

  if (filenameMatch) {
    let filename = filenameMatch[1];
    // Remove quotes if present
    if (filename.startsWith('"') && filename.endsWith('"')) {
      filename = filename.slice(1, -1);
    }
    return filename;
  }

  return null;
};
